import React, { useEffect, useState } from 'react';
import { ReactComponent as MetricIcon } from '../../assets/icons/metrics-icon.svg';
import { ReactComponent as BuildIcon } from '../../assets/icons/build-icon.svg';
import { ReactComponent as MonitorIcon } from '../../assets/icons/eye-icon.svg';
import { ReactComponent as ImproveIcon } from '../../assets/icons/improve-icon.svg';
import { ReactComponent as ArrowIcon } from '../../assets/icons/arrow-large.svg';

import './CopilotGreeting.css';

const CopilotGreeting = ({ icon, title, subtitle, examples: examplesProp, onExampleClick, layout = 'list', columns = 3, rows = 3, maxItems, theme = 'blue', color, bg, bgHover }) => {
    const [loading, setLoading] = useState(false);
    const [greeting, setGreeting] = useState('');
    const [examples, setExamples] = useState(examplesProp ?? []);

    useEffect(() => {
        const getGreeting = () => {
            const hour = new Date().getHours();
            if (hour >= 5 && hour < 12) return 'Good Morning!';
            if (hour >= 12 && hour < 17) return 'Good Afternoon!';
            return 'Good Evening!';
        };

        setGreeting(getGreeting());
    }, []);

    // Update examples when prop changes
    useEffect(() => {
        if (examplesProp) {
            setExamples(examplesProp);
        }
    }, [examplesProp]);

    const handleExampleClick = (example) => {
        if (typeof onExampleClick === 'function') {
            onExampleClick(example);
        }
    };

    // Determine if we have sectioned examples: [{ title, items: [] }, ...]
    const isSectioned = Array.isArray(examples) && examples.length > 0 && typeof examples[0] === 'object' && examples[0] !== null && 'title' in examples[0] && Array.isArray(examples[0].items);

    // Prepare grid data for flat examples only
    const capacity = columns * rows;
    const maxAllowed = (typeof maxItems === 'number' && maxItems > 0) ? maxItems : capacity;
    const limit = Math.min(capacity, maxAllowed, (examples?.length || 0));
    const trimmed = isSectioned ? examples : (examples || []).slice(0, limit);
    const gridColumns = isSectioned
        ? trimmed // use provided sections as columns
        : Array.from({ length: columns }, (_, c) => trimmed.slice(c * rows, c * rows + rows));

    const styleVars = {
        ...(color ? { ['--theme-color']: color } : {}),
        ...(bg ? { ['--theme-bg']: bg } : {}),
        ...(bgHover ? { ['--theme-bg-active']: bgHover } : {}),
    };

    return (
        <div className={`copilot-greeting-container cg-v2 theme-${theme}`} style={styleVars}>
            <div className="greeting-content">
                {icon && (
                    <div className="greeting-icon">
                        {icon}
                    </div>
                )}
                <h1 className="greeting-title">{title || greeting}</h1>
                <h2 className="greeting-subtitle">
                    {subtitle ? (
                        <span dangerouslySetInnerHTML={{__html: subtitle}} />
                    ) : (
                        <span>
                            I'm here to build and manage your cash flow forecast.<br/>How would you like to proceed?
                        </span>
                    )}
                </h2>
                {examples && examples.length > 0 && (
                    isSectioned ? (
                        columns === 1 ? (
                            <div className="sectioned-stacked">
                                {gridColumns.map((section, secIdx) => (
                                    <div className="stacked-section" key={`sec-${secIdx}`}>
                                        <div className="column-header">
                                            {section?.title === 'Build' ? <BuildIcon className="title-icon" /> : section?.title === 'Monitor' ? <MonitorIcon className="title-icon" /> : <ImproveIcon className="title-icon" />}
                                            <h3>{section?.title || ''}</h3>
                                        </div>
                                        <ul className="tile-row">
                                            {(section.items || []).slice(1, 2).map((item, idx) => (
                                                <li key={`${secIdx}-${idx}`} onClick={() => handleExampleClick(item)}>
                                                    {loading ? 'Thinking of questions...' : item}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="columns-container sectioned">
                                {gridColumns.map((section, colIdx) => (
                                    <>
                                        <div className="column" key={`col-${colIdx}`}>
                                            <div className="column-header">
                                                {section?.title === 'Build' ? <BuildIcon className="title-icon" /> : section?.title === 'Monitor' ? <MonitorIcon className="title-icon" /> : <ImproveIcon className="title-icon" />}
                                                <h3>{section?.title || ''}</h3>
                                            </div>
                                            <ul>
                                                {(section.items || []).map((item, idx) => (
                                                    <li key={`${colIdx}-${idx}`} onClick={() => handleExampleClick(item)}>
                                                        {loading ? 'Thinking of questions...' : item}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        {/* {colIdx < gridColumns.length - 1 && (
                                            <span className="arrow-separator" data-pos={colIdx + 1} key={`arrow-${colIdx}`}><ArrowIcon /></span>
                                        )} */}
                                    </>
                                ))}
                            </div>
                        )
                    ) : (
                        layout === 'grid' ? (
                            <div className="columns-container">
                                {gridColumns.map((col, colIdx) => (
                                    <div className="column" key={colIdx}>
                                        <ul>
                                            {col.map((example, idx) => (
                                                <li key={`${colIdx}-${idx}`} onClick={() => handleExampleClick(example)}>
                                                    {loading ? 'Thinking of questions...' : example}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="columns-container">
                                <div className="column">
                                    <ul>
                                        {(examples || []).slice(0, (typeof maxItems === 'number' && maxItems > 0) ? maxItems : examples.length).map((example, index) => (
                                            <li key={index} onClick={() => handleExampleClick(example)}>{loading ? 'Thinking of questions...' : example}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        )
                    )
                )}
            </div>
        </div>
    );
};

export default CopilotGreeting;
